import { MGPOptional, MGPValidation } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { Ordinal } from '../../jscaip/Ordinal';
import { Player, PlayerOrNone } from '../../jscaip/Player';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { MGPValidators } from '../../utils/MGPValidator';
import { SquarzFailure } from './SquarzFailure';
import { SquarzMove } from './SquarzMove';
import { SquarzState } from './SquarzState';
export class SquarzNode extends GameNode {
}
export class SquarzRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static get() {
        if (SquarzRules.singleton.isAbsent()) {
            SquarzRules.singleton = MGPOptional.of(new SquarzRules());
        }
        return SquarzRules.singleton.get();
    }
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Squarz`,
        config: {
            width: new NumberConfig(8, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(3, 99)),
            height: new NumberConfig(8, RulesConfigDescriptionLocalizable.HEIGHT, MGPValidators.range(3, 99)),
            jumpSize: new NumberConfig(2, () => $localize `Jump Size`, MGPValidators.range(2, 99)),
        },
    });
    getRulesConfigDescription() {
        return SquarzRules.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState(config) {
        const width = config.width;
        const height = config.height;
        const board = TableUtils.create(width, height, PlayerOrNone.NONE);
        board[0][0] = Player.ZERO;
        board[height - 1][width - 1] = Player.ZERO;
        board[height - 1][0] = Player.ONE;
        board[0][width - 1] = Player.ONE;
        return new SquarzState(board, 0);
    }
    isLegal(move, state, config) {
        const distance = move.getDistance();
        const jumpSize = config.jumpSize;
        if (jumpSize < distance) {
            return MGPValidation.failure(SquarzFailure.MAX_DISTANCE_IS_N(jumpSize));
        }
        const start = state.getPieceAt(move.getStart());
        const opponent = state.getCurrentOpponent();
        if (start === opponent) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        if (start.isNone()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        const landing = state.getPieceAt(move.getEnd());
        if (landing.isNone()) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
    }
    applyLegalMove(move, state) {
        const start = move.getStart();
        const end = move.getEnd();
        const moveDistance = start.getDistanceToward(end);
        const player = state.getCurrentPlayer();
        const opponent = state.getCurrentOpponent();
        let resultingState = state.setPieceAt(end, player);
        if (moveDistance > 1) {
            resultingState = resultingState.setPieceAt(start, PlayerOrNone.NONE);
        }
        for (const direction of Ordinal.ORDINALS) {
            const neighbor = end.getNext(direction, 1);
            if (resultingState.hasPieceAt(neighbor, opponent)) {
                resultingState = resultingState.setPieceAt(neighbor, player);
            }
        }
        return new SquarzState(resultingState.board, resultingState.turn + 1);
    }
    getGameStatus(node, config) {
        const jumpSize = config.jumpSize;
        const state = node.gameState;
        const currentPlayer = state.getCurrentPlayer();
        if (this.canPlayerMove(state, currentPlayer, jumpSize)) {
            return GameStatus.ONGOING;
        }
        else {
            return this.getDominantPlayerVictory(state);
        }
    }
    canPlayerMove(state, player, jumpSize) {
        for (const coordAndContent of state.getCoordsAndContents()) {
            if (coordAndContent.content.equals(player)) {
                if (state.hasMovablePieceAt(coordAndContent.coord, jumpSize)) {
                    return true;
                }
            }
        }
        return false;
    }
    getPossiblesMoves(state, coord, config) {
        const moves = [];
        const jumpSize = config.jumpSize;
        for (let y = -jumpSize; y <= jumpSize; y++) {
            for (let x = -jumpSize; x <= jumpSize; x++) {
                const landingCoord = new Coord(coord.x + x, coord.y + y);
                if (state.isEmptyAt(landingCoord)) {
                    moves.push(SquarzMove.from(coord, landingCoord).get());
                }
            }
        }
        return moves;
    }
    getDominantPlayerVictory(state) {
        const winner = state.getDominantPlayer();
        if (winner.isPlayer()) {
            return GameStatus.getVictory(winner);
        }
        else {
            return GameStatus.DRAW;
        }
    }
}
